import * as React from 'react';
import {createViewConfig, view, assignProps} from '@sail/engine';
import type {View} from '../view';
import {useCurrentLayer} from './layers/Layer';
import {usePress} from './hooks/usePress';
import type {PressEvents} from './types';
import {useUiConfig} from '../providers/UiConfigProvider';
import {useMediaQuery} from '../providers/UiMediaQueryProvider';

const isNestedOverlay = (
  layerContext: ReturnType<typeof useCurrentLayer> | null | undefined,
): boolean => {
  if (!layerContext) {
    return false;
  }

  if (layerContext.type === 'overlay') {
    return true;
  }

  return isNestedOverlay(layerContext.parent);
};

export type BackdropProps = View.IntrinsicElement<
  'div',
  Pick<PressEvents, 'onPress'> & {
    /**
     * @deprecated Use onPress instead of onClick
     * @ignore
     */
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  }
>;

export const BackdropConfig = createViewConfig({
  props: {} as BackdropProps,
  name: 'Backdrop',
  flattens: true,
});

const isAndroid =
  typeof navigator !== 'undefined' && /(android)/i.test(navigator.userAgent);

function useBackdropOpacity(
  layerContext: ReturnType<typeof useCurrentLayer>,
): number | undefined {
  const uiConfig = useUiConfig();
  const {isTouch, isSmallScreen} = useMediaQuery();
  const isUsingBottomSheets =
    isTouch && isSmallScreen && uiConfig?.enableBottomSheets;

  // For bottom sheets, always use full opacity
  if (isUsingBottomSheets) {
    return 1;
  }

  // For stacked Drawers, set opacity to 0 when in a nested layer
  if (isNestedOverlay(layerContext?.parent)) {
    return 0;
  }

  // By default, the opacity is undefined (will be set by parent component)
  return undefined;
}

export const Backdrop = BackdropConfig.createView(
  ({onPress, ...props}) => {
    const layerContext = useCurrentLayer();
    const ref = React.useRef<HTMLDivElement>(null);
    const opacity = useBackdropOpacity(layerContext);
    const {pressProps} = usePress({
      onPress: (event) => {
        // Temporary disabling backdrop closures on Android devices until we can fix the root
        // cause, which is onClick and onPress events interacting undesirably.
        !isAndroid && onPress?.(event);
      },
      ref,
    });

    return (
      <view.div
        data-testid="backdrop"
        ref={ref}
        uses={[assignProps(pressProps as View.IntrinsicElement<'div'>)]}
        css={{
          opacity,
          // Backdrop in touch devices should never be scrollable, we set `touch-action: none` to prevent tap+drag in the Backdrop to scroll the document's body in iOS.
          // This has no effect when enableBottomSheeets is true, since we already have a mechanism in RootLayer to lock body scroll in iOS. Setting touch-action here
          // still prevents issues when `enableBottomSheets` is not enabled, the deprecated RootLayer is used, or the components are rendered within iframes (Connect).
          // For safety, we only set touch-action to none when the Backdrop has no children, since it is not scrollable in that case, and there are a few cases where
          // the Backdrop is used as a wrapper for other components.
          touchAction: props.children ? 'unset' : 'none',
        }}
        {...props}
      />
    );
  },
  {
    css: {
      backgroundColor: 'backdrop',
      position: 'fixed',
      top: 'space.0',
      left: 'space.0',
      right: 'space.0',
      bottom: 'space.0',
    },
  },
);
